/* The native facets form remains responsible for Shopify requests, counts and history. */
if (!customElements.get('sp-price-range')) {
  customElements.define('sp-price-range', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.amounts = [...this.querySelectorAll('[data-price]')];
      this.sliders = [...this.querySelectorAll('[data-range]')];
      this.limit = Number(this.dataset.max) || 0;
      this.addEventListener('input', (event) => {
        if (this.committing) return;
        event.stopPropagation();
        if (event.target.matches('[data-range]')) {
          const index = this.sliders.indexOf(event.target);
          this.amounts[index].value = event.target.value;
          this.sync(index);
        }
      }, options);
      this.addEventListener('change', (event) => {
        if (!event.target.matches('input')) return;
        const index = event.target.matches('[data-range]') ? this.sliders.indexOf(event.target) : this.amounts.indexOf(event.target);
        this.sync(index);
        this.committing = true;
        this.amounts[index].dispatchEvent(new Event('input', { bubbles: true }));
        this.committing = false;
      }, options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-price]')) {
          event.preventDefault();
          event.target.blur();
        }
      }, options);
      this.sync();
    }
    sync(changed) {
      const values = this.amounts.map((input, index) => input.value === '' ? (index ? this.limit : 0) : Math.max(0, Math.min(this.limit, Number(input.value) || 0)));
      if (values[0] > values[1]) values[changed === 0 ? 0 : 1] = values[changed === 0 ? 1 : 0];
      this.amounts.forEach((input, index) => {
        if (input.value !== '') input.value = Math.round(values[index] * 100) / 100;
        this.sliders[index].value = values[index];
        this.sliders[index].disabled = this.limit === 0;
        this.sliders[index].setAttribute('aria-valuemin', index ? values[0] : 0);
        this.sliders[index].setAttribute('aria-valuemax', index ? this.limit : values[1]);
      });
      const track = this.querySelector('.sp-price-range__track');
      track.style.setProperty('--range-min', `${this.limit ? values[0] / this.limit * 100 : 0}%`);
      track.style.setProperty('--range-max', `${this.limit ? values[1] / this.limit * 100 : 100}%`);
      // Keep either thumb reachable when both are at an endpoint.
      this.sliders[0].style.zIndex = values[0] === this.limit ? '2' : '0';
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; }
  });
}

if (!customElements.get('sp-collection')) {
  customElements.define('sp-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.reference !== 'true' || this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width:640px)');
      this.drawerMode = matchMedia('(max-width:1007px)');
      this.addEventListener('click', (event) => this.onClick(event), { ...options, capture: true });
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        const sort = this.querySelector('.sp-collection-sort[open]');
        if (sort) { sort.open = false; sort.querySelector('summary').focus(); }
        const drawer = this.querySelector('menu-drawer');
        if (drawer?.mainDetailsToggle.open) {
          event.stopPropagation();
          drawer.closeMenuDrawer(event, this.querySelector('[data-open-filters]'));
        }
      }, { ...options, capture: true });
      document.addEventListener('click', (event) => {
        if (!event.target.closest('.sp-collection-sort')) {
          const sort = this.querySelector('.sp-collection-sort[open]');
          if (sort) sort.open = false;
        }
      }, options);
      this.addEventListener('focusout', (event) => {
        const sort = event.target.closest('.sp-collection-sort');
        if (sort && event.relatedTarget && !sort.contains(event.relatedTarget)) sort.open = false;
      }, options);
      this.mobile.addEventListener('change', () => this.updateLayout(), options);
      this.drawerMode.addEventListener('change', () => {
        const drawer = this.querySelector('menu-drawer');
        if (!this.drawerMode.matches && drawer?.mainDetailsToggle.open) drawer.closeMenuDrawer(new Event('resize'));
      }, options);
      this.observer = new MutationObserver(() => this.updateLayout());
      this.observer.observe(this, { childList: true, subtree: true });
      const drawerDetails = this.querySelector('menu-drawer > details');
      drawerDetails?.addEventListener('toggle', () => {
        const open = drawerDetails.open;
        this.querySelector('[data-open-filters]')?.setAttribute('aria-expanded', String(open));
        document.body.classList.toggle('sp-collection-filter-open', open);
      }, options);
      this.updateLayout();
    }
    onClick(event) {
      const layout = event.target.closest('[data-grid]');
      if (layout) {
        const columns = layout.dataset.grid === 'compact' ? (this.mobile.matches ? 1 : 2) : (this.mobile.matches ? 2 : 4);
        this.style.setProperty(this.mobile.matches ? '--sp-collection-mobile-columns' : '--sp-collection-columns', columns);
        this.updateLayout();
      }
      const opener = event.target.closest('[data-open-filters]');
      if (opener) {
        const drawer = this.querySelector('menu-drawer');
        if (!drawer) return;
        drawer.mainDetailsToggle.querySelector('summary').click();
        opener.setAttribute('aria-expanded', 'true');
      }
      const mobileSummary = event.target.closest('.mobile-facets__details > summary');
      if (mobileSummary && this.drawerMode.matches) {
        // Inline accordion, not Dawn's separate slide-in submenu. Keep the outer focus trap.
        event.preventDefault();
        event.stopImmediatePropagation();
        mobileSummary.parentElement.open = !mobileSummary.parentElement.open;
        mobileSummary.setAttribute('aria-expanded', String(mobileSummary.parentElement.open));
      }
      const sortOption = event.target.closest('[data-sort-value]');
      if (sortOption) {
        const select = this.querySelector(this.drawerMode.matches ? '#SortBy-mobile' : '#SortBy');
        if (!select) return;
        select.value = sortOption.dataset.sortValue;
        this.querySelectorAll('[data-sort-value]').forEach(button => button.setAttribute('aria-pressed', String(button === sortOption)));
        const sort = sortOption.closest('details');
        sort.open = false;
        sort.querySelector('summary').focus();
        select.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    updateLayout() {
      const grid = this.querySelector('#product-grid');
      if (!grid) return;
      const columns = Number(getComputedStyle(grid).gridTemplateColumns.split(' ').length);
      this.querySelectorAll('[data-grid]').forEach(button => {
        const compact = button.dataset.grid === 'compact';
        const target = this.mobile.matches ? (compact ? 1 : 2) : (compact ? 2 : 4);
        button.setAttribute('aria-label', `Show ${target} products per row`);
        button.setAttribute('aria-pressed', String(columns === target));
      });
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      document.body.classList.remove('sp-collection-filter-open');
    }
  });
}
